import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Form,
  Tabs,
  Row,
  Col,
  message,
  TreeSelect,
  Space,
  Radio,
  Select,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import { SettingOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Flex } from 'antd-v5';

import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import FormTextInput from '../common/inputs/FormTextInput';
import OntraccrTag from '../common/OntraccrTag';

import BoardDeleteButton from './BoardDeleteButton';
import BoardPermissions from './BoardPermissions/BoardPermissions';
import BoardPinInput from './BoardPinInput';
import BoardWorkflows from './BoardWorkflows';
import BoardFormMappings from './BoardFormMappings';

import {
  deleteBoard,
  getBoardDetails,
  updateBoard,
} from './state/boards.actions';
import { getEmails } from '../emails/state/email.actions';
import { getStatuses } from '../forms/state/forms.actions';

import BoardStatusConfigure from './BoardStatusConfigure';
import BoardColorsConfigure from './BoardColorsConfigure';

import { pinValidator } from '../helpers/validators';
import { filterSelectDropdown, toTitleCase } from '../helpers/helpers';

import Permissions from '../auth/Permissions';
import { getCustomColumnTreeData } from './BoardTimelineView/BoardTimeline.helpers';
import { defaultColumnTreeData } from './BoardTimelineView/BoardTimeline.constants';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import OnTraccrRadioGroup from '../common/inputs/OnTraccrRadioGroup';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { STATUS_FILTERS } from '../subtasks/subtask.constants';

const { TabPane } = Tabs;

const DETAILS_KEY = 'details';

export default function BoardSettings({
  title,
  boardId,
  isPublic,
  divisions,
  creatorId,
  hasPin,
  cardTypeId,
  disableCrossColumnSorting,
  useCardNumberAsCardTitle,
  showCardNumber,
  showListIndex,
  shouldSendStatusChangeEmail,
  shouldSendCardUpdateEmail,
  shouldSendAssignedUsersChangeEmail,
  timelineDisplayFields,
  timelineLinkedFieldId,
  availableOffline,
  sortType,
  enableFormStatusPercentage,
  enableTimeInStatus,
  formTemplateId,
  formStatusId,
  excludedFormStatusIds,
  enableDynamicLastUpdated,
  dynamicLastUpdatedFieldId,
  enableWorkCompletion,
  enableTaskDisplay,
  taskDisplayStatuses,
  shouldShowButtonTitle,
}) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const {
    users = [],
    statuses,
    colors: boardColors = [],
    workflows = [],
    id: selectedBoardId,
  } = useSelector((state) => state.boards.selectedBoard || {});
  const numCards = useSelector((state) => state.boards.cardIds?.length ?? 0);
  const company = useSelector((state) => state.settings.company);
  const formTemplates = useSelector((state) => state.forms.templates);
  const formStatuses = useSelector((state) => state.forms.statuses);
  const largeBoardMode = numCards > 2500;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(DETAILS_KEY);
  const [loading, setLoading] = useState(false);
  const [showColors, setShowColors] = useState(boardColors?.length > 0);
  const [currentColors, setCurrentColors] = useState(boardColors);

  const {
    [cardTypeId]: cardTemplate = {},
  } = useSelector((state) => state.boards.cardTemplates);

  const { userId: ownerId } = company ?? {};

  const formTemplateOptions = useMemo(() => {
    const divSet = new Set(divisions);
    return Object.values(formTemplates)
      .filter((template) => (
        template.active
        && template.notDeleted
        && !template.isExternalForm
        && divSet.has(template.divisionId)
      ))
      .map((template) => ({
        label: template.name,
        value: template.id,
      }));
  }, [formTemplates, divisions]);

  const formStatusList = useMemo(() => (
    Object.values(formStatuses).map((status) => ({
      label: toTitleCase(status.status),
      value: status.id,
    }))
  ), [formStatuses]);

  const dynamicLastUpdatedFieldOptions = useMemo(() => {
    const { fields } = cardTemplate;

    const opts = [];

    fields?.forEach((section) => {
      const { id: sectionId, name, fields: sectionFields } = section ?? {};
      sectionFields?.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            title: fieldTitle,
            dataType,
          } = {},
        } = field ?? {};

        if (selectedType === 'table' && dataType === 'TimeEntry') {
          opts.push({
            label: `${name} - ${fieldTitle}`,
            value: `${sectionId}.${fieldId}`,
          });
        }
      });
    });

    return opts;
  }, [cardTemplate]);

  const sortedStatuses = useMemo(() => {
    const nStatuses = [...statuses ?? []];
    nStatuses.sort((a, b) => a.orderIndex - b.orderIndex);
    return nStatuses;
  }, [statuses]);

  const sortedColors = useMemo(() => {
    const nColors = [...boardColors ?? []];
    nColors.sort((a, b) => a.orderIndex - b.orderIndex);
    return nColors;
  }, [boardColors]);

  const [currentStatuses, setCurrentStatuses] = useState(sortedStatuses);
  const [
    currentEnableFormStatusPercentage,
    setCurrentEnableFormStatusPercentage,
  ] = useState(enableFormStatusPercentage);
  const [currentFormStatusId, setCurrentFormStatusId] = useState(formStatusId);
  const [
    currentExcludedFormStatusIds,
    setCurrentExcludedFormStatusIds,
  ] = useState(excludedFormStatusIds);
  const [
    currentEnableDynamicLastUpdated,
    setCurrentEnableDynamicLastUpdated,
  ] = useState(enableDynamicLastUpdated);
  const [
    currentEnableTimeInStatus,
    setCurrentEnableTimeInStatus,
  ] = useState(enableTimeInStatus);
  const [
    currentEnableTaskDisplay,
    setCurrentEnableTaskDisplay,
  ] = useState(enableTaskDisplay);

  const taskStatusOptions = useMemo(() => (
    STATUS_FILTERS.map((opt) => ({ label: opt.text, value: opt.value }))
  ), []);

  const excludedStatusSet = new Set(currentExcludedFormStatusIds);
  const formStatusOptions = formStatusList.filter(({ value }) => !excludedStatusSet.has(value));
  const excludeStatusOptions = formStatusList.filter(({ value }) => value !== currentFormStatusId);

  const formStatusPercentageCalculationInfo = () => {
    const formStatusLabel = toTitleCase(formStatuses[currentFormStatusId]?.status);

    const excludedLabels = currentExcludedFormStatusIds
      .map((statusId) => toTitleCase(formStatuses[statusId]?.status));

    if (!formStatusLabel) {
      return 'Percentage = (# of forms with selected form status) / (# total forms - # forms in excluded statuses)';
    }

    const denominator = excludedLabels.length > 0
      ? `(# total forms - # forms in ${excludedLabels.map((label) => `"${label}"`).join(', ')})`
      : '(# total forms)';

    return `Percentage = (# of forms with "${formStatusLabel}") / ${denominator}`;
  }

  const columnTreeData = useMemo(() => {
    const customTreeData = getCustomColumnTreeData({ sections: cardTemplate?.fields });
    return [...defaultColumnTreeData, ...customTreeData];
  }, [cardTemplate]);

  const timelineDateFieldOptions = useMemo(() => {
    const { fields } = cardTemplate;

    const opts = [];

    fields?.forEach((section) => {
      const { name, fields: sectionFields } = section ?? {};
      sectionFields?.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            title: fieldTitle,
          } = {},
        } = field ?? {};

        if (selectedType === 'dateRange') {
          opts.push({
            label: `${name} - ${fieldTitle}`,
            value: fieldId,
          });
        }
      });
    });

    return opts;
  }, [cardTemplate]);

  const onValuesChange = useCallback((_, {
    statuses: newStatuses,
    enableFormStatusPercentage: newEnableFormStatusPercentage,
    formStatusId: newFormStatusId,
    excludedFormStatusIds: newExcludedFormStatusIds,
    enableDynamicLastUpdated: newEnableDynamicLastUpdated,
    enableTimeInStatus: newEnableTimeInStatus,
    enableTaskDisplay: newEnableTaskDisplay,
    colors: newColors,
  }) => {
    setCurrentStatuses(newStatuses);
    setCurrentEnableFormStatusPercentage(newEnableFormStatusPercentage);
    setCurrentFormStatusId(newFormStatusId);
    setCurrentExcludedFormStatusIds(newExcludedFormStatusIds ?? []);
    setCurrentEnableDynamicLastUpdated(newEnableDynamicLastUpdated);
    setCurrentEnableTimeInStatus(newEnableTimeInStatus);
    setCurrentEnableTaskDisplay(newEnableTaskDisplay);
    setCurrentColors(newColors);
  }, []);

  const openDrawer = useCallback(() => {
    setDrawerVisible(true);
    setCurrentColors(boardColors);
  }, [boardColors]);
  const closeDrawer = useCallback(() => {
    setDrawerVisible(false);
    setActiveTab(DETAILS_KEY);
    setCurrentColors(boardColors);
    form.resetFields();
  }, [boardColors]);

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      setLoading(true);
      const {
        title: newTitle,
        permissions: {
          isPublic: newIsPublic,
          users: newUsers = [],
        } = {},
        statuses: newStatuses = [],
        pin,
        disableCrossColumnSorting: newColSort,
        showCardNumber: newShowCardNumber,
        showListIndex: newShowListIndex,
        shouldSendStatusChangeEmail: newShouldSendStatusChangeEmail,
        shouldSendCardUpdateEmail: newShouldSendCardUpdateEmail,
        shouldSendAssignedUsersChangeEmail: newShouldSendAssignedUsersChangeEmail,
        availableOffline: newAvailableOffline,
        timelineDisplayFields: newTimelineDisplayFields,
        timelineLinkedFieldId: newTimelineLinkedFieldId,
        workflows: newWorklows = [],
        sortType: newSortType,
        enableFormStatusPercentage: newEnableFormStatusPercentage,
        enableTimeInStatus: newEnableTimeInStatus,
        formTemplateId: newFormTemplateId,
        formStatusId: newFormStatusId,
        excludedFormStatusIds: newExcludedFormStatusIds = [],
        enableDynamicLastUpdated: newEnableDynamicLastUpdated,
        dynamicLastUpdatedFieldId: newDynamicLastUpdatedFieldId,
        enableWorkCompletion: newEnableWorkCompletion,
        enableTaskDisplay: newEnableTaskDisplay,
        taskDisplayStatuses: newTaskDisplayStatuses = [],
        colors: newColors = [],
      } = form.getFieldsValue(true);
      const newUsersPayload = newUsers.map((newUser) => {
        const {
          userId,
          formPermission,
          readPerms,
          writePerms,
          assignPerms,
        } = newUser;
        return {
          userId,
          readPerms,
          writePerms,
          assignPerms,
          formPermission: !!formPermission,
        };
      });
      const payload = {
        title: newTitle,
        isPublic: newIsPublic,
        users: newUsersPayload,
        statuses: newStatuses.map((status) => {
          const payloadStatus = { ...status };
          delete payloadStatus.boardId;
          delete payloadStatus.companyId;
          if (payloadStatus.isNew) delete payloadStatus.id;
          return payloadStatus;
        }),
        pin,
        workflows: newWorklows,
        disableCrossColumnSorting: newColSort,
        showCardNumber: newShowCardNumber,
        showListIndex: newShowListIndex,
        shouldSendStatusChangeEmail: newShouldSendStatusChangeEmail,
        shouldSendCardUpdateEmail: newShouldSendCardUpdateEmail,
        shouldSendAssignedUsersChangeEmail: newShouldSendAssignedUsersChangeEmail,
        availableOffline: largeBoardMode ? false : newAvailableOffline,
        timelineDisplayFields: newTimelineDisplayFields,
        timelineLinkedFieldId: newTimelineLinkedFieldId,
        enableFormStatusPercentage: newEnableFormStatusPercentage,
        enableTimeInStatus: !!newEnableTimeInStatus,
        sortType: newSortType,
        formStatusId: newEnableFormStatusPercentage ? newFormStatusId : null,
        formTemplateId: newEnableFormStatusPercentage
          ? newFormTemplateId
          : null,
        excludedFormStatusIds: newEnableFormStatusPercentage
          ? newExcludedFormStatusIds
          : [],
        enableDynamicLastUpdated: newEnableDynamicLastUpdated,
        dynamicLastUpdatedFieldId: newEnableDynamicLastUpdated
          ? newDynamicLastUpdatedFieldId
          : null,
        enableWorkCompletion: newEnableWorkCompletion,
        enableTaskDisplay: largeBoardMode ? false : newEnableTaskDisplay,
        taskDisplayStatuses: newTaskDisplayStatuses,
        colors: newColors.map((boardColor) => {
          const payloadColor = { ...boardColor };
          delete payloadColor.boardId;
          delete payloadColor.companyId;
          if (payloadColor.isNew) delete payloadColor.id;
          return payloadColor;
        }),
      };
      if (await dispatch(updateBoard(boardId, payload))) {
        if (newEnableWorkCompletion && !enableWorkCompletion) {
          dispatch(getBoardDetails(boardId));
        }
        closeDrawer();
      }
      setLoading(false);
    } catch (err) {
      message.error('Failed to save board');
      Sentry.withScope(() => {
        Sentry.captureException(err);
      });
      setLoading(false);
    }
  }, [
    form,
    boardId,
    closeDrawer,
    users,
    hasPin,
    enableWorkCompletion,
    enableTaskDisplay,
    largeBoardMode,
  ]);

  useEffect(() => {
    dispatch(getEmails());
    dispatch(getStatuses());
  }, []);

  const onDelete = useCallback(async ({ password }) => {
    if (await dispatch(deleteBoard(boardId, password))) {
      closeDrawer();
    }
  }, [dispatch, closeDrawer, boardId]);

  useEffect(() => {
    setCurrentStatuses(sortedStatuses);
    setCurrentEnableFormStatusPercentage(enableFormStatusPercentage);
    setCurrentFormStatusId(formStatusId);
    setCurrentExcludedFormStatusIds(excludedFormStatusIds);
    setCurrentEnableDynamicLastUpdated(enableDynamicLastUpdated);
    setCurrentEnableTimeInStatus(enableTimeInStatus);
    setCurrentEnableTaskDisplay(enableTaskDisplay);
    setShowColors(boardColors?.length > 0);
    form.setFieldsValue({
      title,
      permissions: { isPublic, users },
      statuses: sortedStatuses,
      colors: sortedColors,
      workflows,
      disableCrossColumnSorting,
      showCardNumber,
      showListIndex,
      shouldSendStatusChangeEmail,
      shouldSendCardUpdateEmail,
      shouldSendAssignedUsersChangeEmail,
      availableOffline: largeBoardMode ? false : availableOffline,
      sortType,
      enableFormStatusPercentage,
      enableTimeInStatus: enableTimeInStatus ?? true,
      formTemplateId,
      formStatusId,
      excludedFormStatusIds,
      enableDynamicLastUpdated,
      dynamicLastUpdatedFieldId,
      enableWorkCompletion,
      enableTaskDisplay,
      taskDisplayStatuses: taskDisplayStatuses ?? [],
      timelineLinkedFieldId,
    });
  }, [
    boardId,
    title,
    form,
    drawerVisible,
    isPublic,
    users,
    sortedStatuses,
    sortedColors,
    workflows,
    disableCrossColumnSorting,
    showCardNumber,
    showListIndex,
    shouldSendStatusChangeEmail,
    shouldSendCardUpdateEmail,
    shouldSendAssignedUsersChangeEmail,
    availableOffline,
    largeBoardMode,
    sortType,
    enableFormStatusPercentage,
    enableTimeInStatus,
    formTemplateId,
    formStatusId,
    excludedFormStatusIds,
    enableDynamicLastUpdated,
    dynamicLastUpdatedFieldId,
    enableWorkCompletion,
    enableTaskDisplay,
    taskDisplayStatuses,
    timelineLinkedFieldId,
  ]);

  const onSortTypeChange = useCallback((value) => {
    CustomConfirmModal({
      title: 'Change Sort Type',
      content: `
        Are you sure you want to change the sort type?
        This will reorder all cards in the board for all users and will take time to complete.
      `,
      onConfirm: () => {
        form.setFieldsValue({ sortType: value });
      },
      onCancel: () => {
        form.setFieldsValue({ sortType });
      },
    });
  }, [form, sortType]);

  const canDelete = Permissions.id === creatorId || Permissions.id === ownerId;

  return (
    <>
      <OnTraccrButton
        type="cancel"
        title={shouldShowButtonTitle ? 'Settings' : ''}
        icon={<SettingOutlined style={{ marginLeft: shouldShowButtonTitle ? 8 : 0 }} />}
        onClick={openDrawer}
        disabled={selectedBoardId !== boardId}
      />
      <Drawer
        title={`${title} Settings`}
        width={1160}
        onClose={closeDrawer}
        visible={drawerVisible}
        bodyStyle={{ padding: '0px 24px', maxWidth: 1000 }}
      >
        <Form
          layout="vertical"
          className="boards-setting-form"
          form={form}
          initialValues={{
            title,
            permissions: {
              isPublic,
              users,
            },
            statuses: sortedStatuses,
            colors: sortedColors,
            workflows,
            disableCrossColumnSorting,
            showCardNumber,
            showListIndex,
            shouldSendStatusChangeEmail,
            shouldSendCardUpdateEmail,
            shouldSendAssignedUsersChangeEmail,
            availableOffline,
            timelineDisplayFields,
            timelineLinkedFieldId,
            sortType,
            enableTimeInStatus: enableTimeInStatus ?? true,
            enableWorkCompletion,
            enableTaskDisplay,
            taskDisplayStatuses: taskDisplayStatuses ?? [],
          }}
          onValuesChange={onValuesChange}
        >
          <Tabs tabBarStyle={{ margin: 0 }} activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Details" key={DETAILS_KEY}>
              <FormTextInput
                isNotDisplay
                label="Title"
                name="title"
                value={title}
                rules={[{ required: true, message: 'Please enter a title' }]}
              />
              <Form.Item
                name="pin"
                label="Lock PIN"
                rules={[
                  { validator: pinValidator },
                ]}
                labelCol={{
                  style: { marginTop: 10 },
                }}
              >
                <BoardPinInput initialValue={hasPin} />
              </Form.Item>
              <Form.Item
                name="disableCrossColumnSorting"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Disable Cross Column Sorting"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      Leave this box unchecked if you want to be able to
                      drag and drop to sort cards across columns.
                      <br />
                      <br />
                      If you check this box, new cards will be put at the
                      bottom of the column by default.
                    </div>
                  )}
                />
              </Form.Item>
              <Row style={{
                paddingRight: 50,
                display: 'flex',
                flexDirection: 'row',
                gap: 30,
              }}
              >
                <Col style={{
                  flexBasis: '50%',
                }}
                >
                  <Form.Item
                    name="showCardNumber"
                    style={{ paddingLeft: 5 }}
                  >
                    <OnTraccrCheckbox
                      label="Show Card Number"
                      hoverNode={(
                        <div style={{ width: 300 }}>
                          If you check this box, cards will show a
                          unique number for their type.
                        </div>
                      )}
                      disabled={useCardNumberAsCardTitle}
                    />
                  </Form.Item>
                  <Form.Item
                    name="showListIndex"
                    style={{ paddingLeft: 5 }}
                  >
                    <OnTraccrCheckbox
                      label="Show List Index Position"
                      hoverNode={(
                        <div style={{ width: 300 }}>
                          If you check this box, cards in list view
                          will show their position number.
                        </div>
                      )}
                    />
                  </Form.Item>
                </Col>

              </Row>

              <Form.Item
                name="shouldSendStatusChangeEmail"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Send Email on Status Change"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      If you check this box, an email will be sent
                      to the assigned users when a card&apos;s status is changed.
                    </div>
                  )}
                />
              </Form.Item>
              <Form.Item
                name="shouldSendCardUpdateEmail"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Send Email on Card Update"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      If you check this box, an email will be sent
                      to the assigned users when a card is updated.
                    </div>
                  )}
                />
              </Form.Item>
              <Form.Item
                name="shouldSendAssignedUsersChangeEmail"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Send Email on Card Assigned User Change"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      If you check this box, an email will be sent
                      to the all current, added, and removed users
                      when a card&apos;s assigned users is changed.
                    </div>
                  )}
                />
              </Form.Item>
              <Form.Item
                name="availableOffline"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label={(
                    <>
                      Available Offline
                      <OntraccrTag color="orange" style={{ marginLeft: 5 }}>Beta Feature</OntraccrTag>
                    </>
                  )}
                  disabled={largeBoardMode}
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      {largeBoardMode
                        ? 'Offline mode is not available for High Volume Mode boards.'
                        : 'If you check this box, this board and its associated cards will be available for offline use on the mobile application.'}
                    </div>
                  )}
                />
              </Form.Item>
              <Form.Item
                name="enableTimeInStatus"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Show time in status on cards"
                />
              </Form.Item>
              <Form.Item
                name="enableFormStatusPercentage"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Show form status percentage on cards"
                />
              </Form.Item>
              {
                currentEnableFormStatusPercentage && (
                  <Flex vertical>
                    <Form.Item
                      label="Form Workflow"
                      name="formTemplateId"
                    >
                      <Select
                        options={formTemplateOptions}
                        placeholder="Select Form Workflow"
                        showSearch
                        allowClear
                        filterOption={filterSelectDropdown}
                        optionFilterProp="label"
                      />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
                      {formStatusPercentageCalculationInfo()}
                    </Typography.Text>
                    <Flex gap={16} justify="space-between">
                      <Form.Item
                        label="Form Status"
                        name="formStatusId"
                        style={{ width: '50%' }}
                      >
                        <Select
                          options={formStatusOptions}
                          placeholder="Select Form Status"
                          allowClear
                        />
                      </Form.Item>
                      <Form.Item
                        label="Exclude Statuses"
                        name="excludedFormStatusIds"
                        style={{ width: '50%' }}
                      >
                        <Select
                          mode="multiple"
                          options={excludeStatusOptions}
                          placeholder="Select Statuses to Exclude"
                        />
                      </Form.Item>
                    </Flex>
                  </Flex>
                )
              }
              <Form.Item
                name="enableDynamicLastUpdated"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Show Dynamic Last Updated Timestamp on Cards"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      This checkbox will allow you to select a Time Entry Table Field
                      to display a last updated timestamp on Cards.
                      A Time Entry Table Field must be selected for the display to work.
                    </div>
                  )}
                />
              </Form.Item>
              {currentEnableDynamicLastUpdated && (
                <Form.Item
                  label="Dynamic Last Updated Timestamp"
                  name="dynamicLastUpdatedFieldId"
                >
                  <Select
                    options={dynamicLastUpdatedFieldOptions}
                    placeholder="Select Dynamic Last Updated Timestamp"
                  />
                </Form.Item>
              )}
              <Form.Item
                name="enableWorkCompletion"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Track Work Completion"
                />
              </Form.Item>
              <Form.Item
                name="enableTaskDisplay"
                style={{ paddingLeft: 5 }}
              >
                <OnTraccrCheckbox
                  label="Show tasks on cards"
                  disabled={largeBoardMode}
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      {largeBoardMode
                        ? 'Task display is not available for High Volume Mode boards.'
                        : 'Display associated tasks on board cards. Up to 3 tasks are shown per card.'}
                    </div>
                  )}
                />
              </Form.Item>
              {currentEnableTaskDisplay && (
                <Form.Item
                  label="Task statuses to display"
                  name="taskDisplayStatuses"
                >
                  <Select
                    mode="multiple"
                    options={taskStatusOptions}
                    placeholder="Select task statuses"
                  />
                </Form.Item>
              )}
              <OnTraccrCheckbox
                label="Show Color Legend"
                onChange={setShowColors}
                value={showColors}
                disabled={currentColors?.length > 0}
                style={{ paddingLeft: 6, marginBottom: 10 }}
                hoverNode={(
                  <div style={{ width: 300 }}>
                    This checkbox will allow you to show a color legend on the board.
                    <br />
                    The color legend can be used to standardize colors for cards in the board
                  </div>
                )}
              />
              {
                showColors && (
                  <Form.Item
                    label="Colors"
                    name="colors"
                  >
                    <BoardColorsConfigure />
                  </Form.Item>
                )
              }
              <Form.Item
                label="Statuses"
                name="statuses"
              >
                <BoardStatusConfigure cardTypeId={cardTypeId} />
              </Form.Item>
              <Form.Item
                label="Timeline Columns"
                name="timelineDisplayFields"
              >
                <TreeSelect
                  treeData={columnTreeData}
                  treeDefaultExpandAll
                  multiple
                  showCheckedStrategy="SHOW_PARENT"
                  placeholder="Select Columns"
                  style={{
                    width: '100%',
                    flexGrow: 4,
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Timeline Date"
                name="timelineLinkedFieldId"
              >
                <Select
                  options={timelineDateFieldOptions}
                  placeholder="Select Date Range Field"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                label="Sort Cards"
                name="sortType"
              >
                <OnTraccrRadioGroup
                  onChange={onSortTypeChange}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <Space direction="vertical">
                    <Radio value="default">Default</Radio>
                    <Radio value="title-asc">Title: A to Z</Radio>
                    <Radio value="title-desc">Title: Z to A</Radio>
                    <Radio value="lastMoveTimestamp-asc">Time in Status: Ascending</Radio>
                    <Radio value="lastMoveTimestamp-desc">Time in Status: Descending</Radio>
                  </Space>
                </OnTraccrRadioGroup>
              </Form.Item>
            </TabPane>
            <TabPane tab="Permissions" key="permissions">
              <Form.Item name="permissions">
                <BoardPermissions
                  divisions={divisions}
                  creatorId={creatorId}
                  visible={drawerVisible}
                />
              </Form.Item>
            </TabPane>
            <TabPane tab="Workflows" key="workflows">
              <Form.Item name="workflows">
                <BoardWorkflows
                  boardId={boardId}
                  cardTypeId={cardTypeId}
                  currentStatuses={currentStatuses}
                />
              </Form.Item>
            </TabPane>
            <TabPane tab="Forms" key="forms">
              <Form.Item name="formMappings">
                <BoardFormMappings
                  boardId={boardId}
                  divisions={divisions}
                  cardTypeId={cardTypeId}
                  currentStatuses={currentStatuses}
                />
              </Form.Item>
            </TabPane>
          </Tabs>
        </Form>
        <DrawerSubmitFooter
          onClose={closeDrawer}
          onSubmit={onSubmit}
          loading={loading}
        />
        {canDelete && (
          <BoardDeleteButton
            title={title}
            onDelete={onDelete}
          />
        )}
      </Drawer>
    </>
  );
}

BoardSettings.propTypes = {
  title: PropTypes.string,
  boardId: PropTypes.string,
  isPublic: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
  creatorId: PropTypes.string,
  hasPin: PropTypes.number,
  cardTypeId: PropTypes.number,
  disableCrossColumnSorting: PropTypes.bool,
  useCardNumberAsCardTitle: PropTypes.bool,
  showCardNumber: PropTypes.bool,
  showListIndex: PropTypes.bool,
  shouldSendStatusChangeEmail: PropTypes.bool,
  shouldSendCardUpdateEmail: PropTypes.bool,
  shouldSendAssignedUsersChangeEmail: PropTypes.bool,
  timelineDisplayFields: PropTypes.arrayOf(PropTypes.string),
  timelineLinkedFieldId: PropTypes.string,
  availableOffline: PropTypes.bool,
  sortType: PropTypes.string,
  enableFormStatusPercentage: PropTypes.bool.isRequired,
  enableTimeInStatus: PropTypes.bool,
  formTemplateId: PropTypes.string,
  formStatusId: PropTypes.number,
  excludedFormStatusIds: PropTypes.arrayOf(PropTypes.number),
  enableDynamicLastUpdated: PropTypes.bool.isRequired,
  dynamicLastUpdatedFieldId: PropTypes.string,
  enableWorkCompletion: PropTypes.bool,
  enableTaskDisplay: PropTypes.bool,
  taskDisplayStatuses: PropTypes.arrayOf(PropTypes.string),
  shouldShowButtonTitle: PropTypes.bool,
};

BoardSettings.defaultProps = {
  title: null,
  boardId: null,
  isPublic: false,
  divisions: [],
  creatorId: null,
  hasPin: false,
  cardTypeId: null,
  disableCrossColumnSorting: false,
  useCardNumberAsCardTitle: false,
  showCardNumber: false,
  showListIndex: false,
  shouldSendStatusChangeEmail: false,
  shouldSendCardUpdateEmail: false,
  shouldSendAssignedUsersChangeEmail: false,
  timelineDisplayFields: [],
  timelineLinkedFieldId: null,
  availableOffline: false,
  sortType: 'default',
  enableTimeInStatus: true,
  formTemplateId: null,
  formStatusId: null,
  excludedFormStatusIds: [],
  dynamicLastUpdatedFieldId: null,
  enableWorkCompletion: false,
  enableTaskDisplay: false,
  taskDisplayStatuses: [],
  shouldShowButtonTitle: true,
};
